import {
  DataModel, SerializationContext, SerializedData, DataModelBuilder,
  cloneListOfCloneable, isSameList, isSameData, cloneMap, isSameMap, mapToData,
} from '../../dto/dto.js';
import { MapUtils } from '../../../utility/map-utils.js';
import { IDataComparable } from '../../../contracts/data-comparable.js';
import { ICloneable } from '../../../contracts/cloneable.js';
import { CapacityRoom } from './capacity-room.js';
import { Building } from './building.js';

export class BuildingSearchEntry implements IDataComparable, ICloneable {
  constructor(
    public readonly uniqueId: string,
    public readonly name: string,
  ) {
  }

  static fromBuilding(building: Building): BuildingSearchEntry {
    return new BuildingSearchEntry(building.uniqueId, building.name);
  }

  static fromMap(context: SerializationContext, data: SerializedData): BuildingSearchEntry {
    return new BuildingSearchEntry(
        MapUtils.get<string>(data, 'uniqueId'),
        MapUtils.get<string>(data, 'name'),
    );
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      'uniqueId': this.uniqueId,
      'name': this.name,
    };
  }

  clone(): this {
    return new BuildingSearchEntry(
        this.uniqueId,
        this.name,
    ) as this;
  }

  isSame(other: this): boolean {
    if(other === this) {
      return true;
    }

    return this.uniqueId === other.uniqueId &&
        this.name === other.name;
  }
}

export class RoomSearchEntry implements IDataComparable, ICloneable {
  constructor(
    public readonly uniqueId: string,
    public readonly buildingId: string,
    public readonly name: string,
  ) {
  }

  static fromRoom(room: CapacityRoom): RoomSearchEntry {
    return new RoomSearchEntry(room.uniqueId, room.buildingId, room.name);
  }

  static fromMap(context: SerializationContext, data: SerializedData): RoomSearchEntry {
    return new RoomSearchEntry(
        MapUtils.get<string>(data, 'uniqueId'),
        MapUtils.get<string>(data, 'buildingId'),
        MapUtils.get<string>(data, 'name'),
    );
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      'uniqueId': this.uniqueId,
      'buildingId': this.buildingId,
      'name': this.name,
    };
  }

  clone(): this {
    return new RoomSearchEntry(
        this.uniqueId,
        this.buildingId,
        this.name,
    ) as this;
  }

  isSame(other: this): boolean {
    if(other === this) {
      return true;
    }

    return this.uniqueId === other.uniqueId &&
        this.buildingId === other.buildingId &&
        this.name === other.name;
  }
}

export class RoomSearchData extends DataModel {
  static readonly TYPE_ID: string = '7aed3fd30bd54a178a1f1ee3e7114f4b';

  constructor(
    public readonly buildings: BuildingSearchEntry[],
    public readonly rooms: Map<string, RoomSearchEntry[]>,
  ) {
    super();
  }

  get $typeId(): string {
    return RoomSearchData.TYPE_ID;
  }

  static empty(): RoomSearchData {
    return new RoomSearchData(
      new Array<BuildingSearchEntry>(),
      new Map<string, RoomSearchEntry[]>(),
    );
  }

  static fromMap(context: SerializationContext, map: SerializedData): RoomSearchData {
    return new RoomSearchData(
        MapUtils.getList(map, 'buildings', (v) => BuildingSearchEntry.fromMap(context, v as SerializedData)),
        MapUtils.getMap(map, 'rooms', (k, v) => {
          const list = v as SerializedData[];
          return { key: k, value: list.map((e) => RoomSearchEntry.fromMap(context, e)) };
        }),
    );
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      'buildings': this.buildings.map((e) => e.toMap(context)),
      'rooms': mapToData(this.rooms, (k, v) => ({ key: k, value: v.map((e) => e.toMap(context)) })),
    };
  }

  clone(): this {
    return new RoomSearchData(
        cloneListOfCloneable(this.buildings),
        cloneMap(this.rooms, (rooms) => cloneListOfCloneable(rooms)),
    ) as this;
  }

  isSame(other: this): boolean {
    if(other === this) {
      return true;
    }

    return isSameList(this.buildings, other.buildings, isSameData) &&
        isSameMap(this.rooms, other.rooms, (a, b) => isSameList(a, b, isSameData));
  }
}

export class RoomSearchDataBuilder extends DataModelBuilder<RoomSearchData> {
  build(context: SerializationContext, map: SerializedData): RoomSearchData {
    return RoomSearchData.fromMap(context, map);
  }
}
